import { VALIDATION } from "./constants";

/**
 * URL validation and manipulation utilities
 */

/**
 * Validates if a string is a valid HTTP(S) URL
 * @param str - String to validate
 * @returns true if valid URL, false otherwise
 */
export function isValidUrl(str: string): boolean {
  const trimmed = str.trim();
  if (!trimmed) return false;
  
  try {
    const urlObj = new URL(trimmed);
    return (VALIDATION.ALLOWED_PROTOCOLS as readonly string[]).includes(urlObj.protocol);
  } catch {
    return false;
  }
}

/**
 * Makes a relative URL absolute
 * @param url - URL to process (can be relative or absolute)
 * @param baseUrl - Base URL to use for relative URLs
 * @returns Absolute URL
 */
export function makeAbsoluteUrl(url: string, baseUrl: string): string {
  if (url.startsWith('http')) return url;
  
  try {
    const base = new URL(baseUrl);
    if (url.startsWith('//')) {
      return `${base.protocol}${url}`;
    } else if (url.startsWith('/')) {
      return `${base.protocol}//${base.host}${url}`;
    } else {
      return new URL(url, baseUrl).href;
    }
  } catch (error) {
    return url; // Return original if conversion fails
  }
}

/**
 * Extracts hostname from URL
 * @param url - URL to extract from
 * @returns Hostname without 'www.' prefix
 */
export function getHostname(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return null;
  }
}

/**
 * Formats hostname as a readable merchant name
 * @param hostname - Hostname to format
 * @returns Capitalized merchant name
 */
export function formatMerchantName(hostname: string): string {
  return hostname
    .split('.')[0]
    .split(/[-_]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
